import { supabase } from '../utils/supabaseClient';

const normalizeIDType = (value) => {
  const normalized = (value || '').toLowerCase().trim().replace(/[_\s-]+/g, '_');

  if (normalized === 'aadhaar' || normalized === 'aadhar') return 'aadhaar';
  if (normalized === 'pan') return 'pan';
  if (normalized === 'passport') return 'passport';
  if (
    normalized === 'driving_license' ||
    normalized === 'driving_licence' ||
    normalized === 'drivinglicense' ||
    normalized === 'drivinglicence' ||
    normalized === 'license' ||
    normalized === 'licence' ||
    normalized === 'dl'
  ) {
    return 'driving_license';
  }

  return null;
};

const normalizeIDNumber = (value) => {
  if (!value) return '';
  return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
};

export function validateExtractedID(data) {
  const normalizedData = { ...(data || {}) };
  const errors = [];

  const idType = normalizeIDType(normalizedData.idType || '');
  const idNumber = normalizeIDNumber(normalizedData.idNumber);

  if (!idType) {
    errors.push('Unsupported or missing ID type.');
  }

  if (!idNumber) {
    errors.push('ID number could not be extracted.');
  }

  if (idType === 'aadhaar') {
    if (!/^\d{12}$/.test(idNumber)) {
      errors.push('Aadhaar number must be exactly 12 digits.');
    }
  }

  if (idType === 'pan') {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(idNumber)) {
      errors.push('PAN format must be 5 letters, 4 digits, and 1 letter.');
    }
  }

  if (idType === 'passport') {
    if (!/^[A-Z0-9]{8,9}$/.test(idNumber)) {
      errors.push('Passport number must be 8 to 9 alphanumeric characters.');
    }
  }

  if (idType === 'driving_license') {
    if (!/^[A-Z0-9]{10,16}$/.test(idNumber)) {
      errors.push('Driving license number must be 10 to 16 alphanumeric characters.');
    }
  }

  normalizedData.idType = idType || normalizedData.idType;
  normalizedData.idNumber = idNumber;

  return {
    valid: errors.length === 0,
    errors,
    normalizedData,
    message:
      errors.length === 0
        ? 'ID data validation successful.'
        : `Validation failed: ${errors.join(' ')}`,
  };
}

export async function uploadIDDocument(userId, file, data, text) {
  try {
    if (!userId) {
      return {
        success: false,
        message: 'User ID is required to upload an ID document.',
      };
    }

    if (!file) {
      return {
        success: false,
        message: 'No file was selected for upload.',
      };
    }

    const validation = validateExtractedID(data || null);

    if (!validation.valid) {
      return {
        success: false,
        message: validation.message,
        errors: validation.errors,
      };
    }

    const safeUserId = String(userId).trim();
    const fileName = file.name || `id-document-${Date.now()}.jpg`;
    const extension = fileName.includes('.') ? fileName.split('.').pop() : 'jpg';
    const storagePath = `${safeUserId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('id_documents')
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      if (uploadError.message?.toLowerCase().includes('bucket not found')) {
        return {
          success: false,
          message: 'The Supabase Storage bucket "id_documents" does not exist. Create this bucket in Supabase Storage, then try again.',
          error: uploadError,
        };
      }
      throw uploadError;
    }

    // Store the storage path instead of URL (will generate signed URLs when needed)
    const documentType = validation.normalizedData.idType || 'aadhaar';
    const documentNumber = validation.normalizedData.idNumber || '';
    const insertPayload = {
      user_id: safeUserId,
      id_type: documentType,
      document_type: documentType,
      id_number: documentNumber,
      document_number: documentNumber,
      holder_name: data?.name?.trim() || null,
      file_url: storagePath, // Store path instead of URL
      status: 'pending',
      created_at: new Date().toISOString(),
    };

    const { data: insertedDocument, error: dbError } = await supabase
      .from('id_documents')
      .insert(insertPayload)
      .select()
      .single();

    if (dbError) {
      throw dbError;
    }

    // Update user's ID verification status to pending (only if not already pending)
    const { error: userUpdateError } = await supabase
      .from('users')
      .update({ 
        id_verification_status: 'pending',
        id_rejected_reason: null 
      })
      .eq('id', safeUserId);

    if (userUpdateError) {
      console.warn('Failed to update user ID verification status:', userUpdateError);
      // Don't throw error here - the document was already uploaded successfully
    }

    return {
      success: true,
      message: 'ID document uploaded and stored successfully.',
      document: insertedDocument,
      fileUrl: insertPayload.file_url,
      status: 'pending',
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || 'Failed to upload the ID document.',
      error: error?.message || 'Failed to upload the ID document.',
    };
  }
}

export function maskIDNumber(idNumber, idType) {
  if (!idNumber) return '';

  const cleanValue = String(idNumber).replace(/\s+/g, '').trim();
  const safeType = normalizeIDType(idType || '') || 'aadhaar';
  const lastFour = cleanValue.slice(-4) || 'XXXX';

  if (safeType === 'aadhaar') {
    if (cleanValue.length <= 4) return `XXXX${lastFour}`;
    return `XXXX XXXX ${lastFour}`;
  }

  if (safeType === 'pan') {
    if (cleanValue.length <= 4) return `XXXX${lastFour}`;
    const prefix = cleanValue.slice(0, 2).toUpperCase();
    const maskedLength = Math.max(0, cleanValue.length - 6);
    return `${prefix}${'X'.repeat(maskedLength)}${lastFour}`;
  }

  if (safeType === 'passport') {
    if (cleanValue.length <= 4) return `XXXX${lastFour}`;
    return `${cleanValue.slice(0, 2).toUpperCase()}XXXX${lastFour}`;
  }

  if (safeType === 'driving_license') {
    if (cleanValue.length <= 4) return `XXXX${lastFour}`;
    return `XXXX XXXX ${lastFour}`;
  }

  if (cleanValue.length <= 4) return `XXXX${lastFour}`;
  return `${'*'.repeat(cleanValue.length - 4)}${lastFour}`;
}

export async function getUserIDVerification(userId) {
  try {
    if (!userId) {
      return {
        success: false,
        status: 'not_found',
        message: 'User ID is required to fetch verification status.',
        data: null,
      };
    }

    const { data, error } = await supabase
      .from('id_documents')
      .select('*')
      .eq('user_id', String(userId))
      .order('created_at', { ascending: false });

    if (error) throw error;

    const latestDocument = data?.[0] || null;
    const status = latestDocument?.status || 'not_uploaded';

    return {
      success: true,
      status,
      isVerified: status === 'verified',
      message:
        latestDocument
          ? `Verification status: ${status}`
          : 'No ID verification record found for this user.',
      data: latestDocument,
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      message: error?.message || 'Unable to fetch user ID verification status.',
      data: null,
    };
  }
}